import { EHttpMethods } from "../../../../enums";
import { AUTH_BASE_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import {
  LIST_ORGANIZATIONS_URL,
  ORGANISATIONS_ADMIN_URL,
  PAYMENT_TYPES_URL,
} from "./constants";
import {
  IOrganizationBankInfoResponse,
  IOrganizationDocumentationResponse,
  IOrganizationTierResponse,
  IOrganizationFeeTemplate,
  IOrganizationPaymentFee,
  IAddOrganizationFeeTemplateBody,
  IOrganizationPaymentTypeResponse,
  IPaymentTypesResponse,
  IUpdateTierFeesBody,
  IOrganizationsListResponse,
  IOrganizationDetailResponse,
  INewOrganizationBody,
  IEditOrganizationBody,
  IDeleteOrganizationBody,
  IBaseApiResponse,
  IAddOrganizationDocumentationBody,
} from "./models";
import { IAddNewUserBody, IClientAdminResponse } from "./types";
import { TOrganizationVerificationStatus } from "../../../types/Organisation";

// eslint-disable-next-line no-unused-vars
const defaultProvide = async () => await getToken();

export const getAllOrganizations = (
  page = 0,
  size = 10,
  searchValue = "",
  verificationStatus?:
    | TOrganizationVerificationStatus
    | TOrganizationVerificationStatus[]
): Promise<IOrganizationsListResponse> => {
  const url = {
    get value() {
      let base = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}?page=${page}&size=${size}&sort=createdAt,DESC`;
      if (searchValue !== "") {
        const encoded = searchValue.replace("+", "%2B");
        base += `&searchKeyword=${encoded}`;
      }
      if (Array.isArray(verificationStatus)) {
        if (verificationStatus.length > 0) {
          base += `&verificationStatuses=${verificationStatus.join(",")}`;
        }
      } else if (verificationStatus) {
        base += `&verificationStatus=${verificationStatus}`;
      }
      return base;
    },
  };

  const descriptor = new ApiServiceManager(
    url.value,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Approves a PENDING organization: grants ORGANIZATION_OWNER, pushes KC claims, creates a default fee template. */
export const verifyOrganization = (
  code: string
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/verify`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** Rejects a PENDING organization with a reason. */
export const rejectOrganization = (
  code: string,
  rejectReason: string
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/reject`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, { rejectReason });
};

export const addNewOrganization = (
  body: INewOrganizationBody
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addNewOrganizationUser = (
  body: IAddNewUserBody
): Promise<IClientAdminResponse> => {
  const url = `${AUTH_BASE_URL}${ORGANISATIONS_ADMIN_URL}/organization-admin-sign-up`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const editOrganization = (
  code: string,
  body: IEditOrganizationBody
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PATCH,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const deleteOrganizationAPI = (
  code: string,
  body: IDeleteOrganizationBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getOrganization = (
  code: string
): Promise<IOrganizationDetailResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationPaymentMethods = (
  code: string
): Promise<IOrganizationPaymentTypeResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/paymentTypes`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

/** The payment types (gateways) known to the backend, with the database ids that fee records
 * have to reference. */
export const getPaymentTypes = (): Promise<IPaymentTypesResponse> => {
  const url = `${AUTH_BASE_URL}${PAYMENT_TYPES_URL}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationBanks = (
  code: string
): Promise<IOrganizationBankInfoResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/banks`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationDocumentations = (
  code: string
): Promise<IOrganizationDocumentationResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const addOrganizationDocumentation = (
  code: string,
  body: IAddOrganizationDocumentationBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const updateOrganizationDocumentation = (
  code: string,
  id: number,
  body: IAddOrganizationDocumentationBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const deleteOrganizationDocumentation = (
  code: string,
  id: number
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/attachments/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.DELETE,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const getOrganizationTiers = (
  code: string
): Promise<IOrganizationTierResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/fee-templates/active/details`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const addBulkOrganizationPaymentTypeFee = (
  code: string,
  body: IOrganizationPaymentFee[]
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/bulk-payment-types-fee`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const getOrganizationSettings = (
  code: string
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/settings`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.GET,
    defaultProvide
  );
  return fetchRequest(descriptor);
};

export const updateOrganizationSettings = (
  code: string,
  body: string[]
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/settings`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addOrganizationFeeTemplate = (
  code: string,
  body: IAddOrganizationFeeTemplateBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/fee-templates`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const addOrganizationPaymentTypeFee = (
  code: string,
  body: IOrganizationPaymentFee
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/payment-types-fee`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.POST,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const updateOrganizationPaymentTypeFees = (
  code: string,
  body: IUpdateTierFeesBody
): Promise<IBaseApiResponse> => {
  const url = `${AUTH_BASE_URL}${LIST_ORGANIZATIONS_URL}/${code}/payment-types-fee`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};

export const updateFeeTemplate = (
  id: number,
  body: Partial<IOrganizationFeeTemplate>
): Promise<IOrganizationTierResponse> => {
  const url = `${AUTH_BASE_URL}/api/organizations/fee-templates/${id}`;
  const descriptor = new ApiServiceManager(
    url,
    EHttpMethods.PUT,
    defaultProvide
  );
  return fetchRequest(descriptor, body);
};
